import { useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Music } from "lucide-react";
import { api, errMsg, GENRES } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

const getDuration = (file) =>
  new Promise((res) => {
    const a = new Audio();
    a.preload = "metadata";
    a.onloadedmetadata = () => { res(a.duration || 180); URL.revokeObjectURL(a.src); };
    a.onerror = () => res(180);
    a.src = URL.createObjectURL(file);
  });

const STATUS = { approved: "text-[#00F5D4] border-[#00F5D4]/50", pending: "text-[#FFB800] border-[#FFB800]/50", rejected: "text-[#FF2E63] border-[#FF2E63]/50" };

export const Tracks = ({ d, reload }) => {
  const [f, setF] = useState({ title: "", genre: d.user.genre || "", rights: false, file: null });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const max = d.plan.max_tracks;
  const full = d.tracks.length >= max;

  const submit = async (e) => {
    e.preventDefault();
    if (!f.file) return toast.error("Choose an MP3 file");
    if (!f.rights) return toast.error("Please confirm you own the rights");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f.file);
      fd.append("title", f.title);
      fd.append("genre", f.genre);
      fd.append("duration", String(await getDuration(f.file)));
      fd.append("rights", "true");
      await api.post("/me/tracks", fd);
      toast.success("Track uploaded — it goes on air once approved");
      setF({ ...f, title: "", file: null, rights: false });
      reload();
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  const remove = async (id) => { await api.delete(`/me/tracks/${id}`); toast.success("Track removed"); reload(); };

  return (
    <div className="grid gap-8 lg:grid-cols-12" data-testid="dash-tracks">
      <div className="space-y-3 lg:col-span-7">
        <div className="label-mono">Your tracks · {d.tracks.length}/{max > 100 ? "∞" : max}</div>
        {d.tracks.map((t) => (
          <div key={t.id} className="panel flex items-center gap-4 p-4" data-testid={`track-item-${t.id}`}>
            <Music className="h-5 w-5 text-[#00F5D4]" />
            <div className="min-w-0 flex-1"><div className="truncate font-display font-bold">{t.title}</div><div className="font-mono text-xs uppercase text-slate-500">{t.genre} · {Math.round(t.duration / 60)} min</div></div>
            <span className={`rounded-full border px-2.5 py-0.5 font-mono text-[10px] uppercase ${STATUS[t.status]}`} data-testid={`track-status-${t.id}`}>{t.status === "pending" ? "In review" : t.status}</span>
            <button onClick={() => remove(t.id)} className="text-slate-500 hover:text-[#FF2E63]" data-testid={`track-delete-${t.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {!d.tracks.length && <p className="text-slate-500">No tracks yet. Upload your best one — that's the one strangers will judge blind.</p>}
      </div>
      <form onSubmit={submit} className="panel console-corners space-y-4 p-6 lg:col-span-5" data-testid="track-upload-form">
        <div className="label-mono">Upload a track</div>
        {full ? <p className="text-sm text-slate-400" data-testid="track-limit-msg">Your {d.plan.name} plan allows {max} track(s). Remove one or upgrade to add more.</p> : <>
          <button type="button" onClick={() => fileRef.current.click()} data-testid="track-file-button" className="flex w-full flex-col items-center gap-2 rounded-xl border border-dashed border-white/20 p-6 text-sm text-slate-400 hover:border-[#00F5D4]/60">
            <Upload className="h-6 w-6 text-[#00F5D4]" />{f.file ? f.file.name : "Choose MP3 (max 25 MB)"}
          </button>
          <input ref={fileRef} hidden type="file" accept="audio/mpeg,.mp3" data-testid="track-file-input" onChange={(e) => setF({ ...f, file: e.target.files[0], title: f.title || (e.target.files[0]?.name || "").replace(/\.mp3$/i, "") })} />
          <div><Label>Title</Label><Input data-testid="track-title-input" required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="h-11 border-white/10 bg-[#07090E]" /></div>
          <div><Label>Genre</Label>
            <select data-testid="track-genre-select" value={f.genre} onChange={(e) => setF({ ...f, genre: e.target.value })} className="h-11 w-full rounded-md border border-white/10 bg-[#07090E] px-3 text-sm capitalize">
              {GENRES.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <label className="flex items-start gap-3 text-sm text-slate-300">
            <Checkbox data-testid="track-rights-checkbox" checked={f.rights} onCheckedChange={(v) => setF({ ...f, rights: !!v })} className="mt-0.5 border-white/30" />
            I own the rights to this track (music, lyrics and recording) and allow Fuorionda to broadcast it.
          </label>
          <button className="btn-hot w-full" disabled={busy} data-testid="track-upload-submit">{busy ? "Uploading…" : "Upload track"}</button>
        </>}
      </form>
    </div>
  );
};
